/**
 * FILE-OVERVIEW:
 * - 役割: 会議資料画面。右の一覧から選んだテーマを種別ごとに並べ、A4/A3の1枚に収めて印刷する。
 * - 主な連携: services/s3Service.ts（listTodayContents / groupContentsByTheme）, utils/meetingSheet.ts
 * - 本番運用上の注意: 読み取り専用。メモ画面の「会議資料」ボタンから新しいタブで開く（?date=YYYY-MM-DD）。
 *   用紙は空から始め、クリックかドラッグで入れる。選んだ内容は日付ごとに localStorage に残す
 *   （閲覧者ごとの作業状態。消えても選び直せばよい）。
 *   段組みにしたうえで1枚に収まる文字サイズを実測して決める。
 *   印刷は body 直下に複製した用紙だけを刷る。印刷時はビューポートが用紙幅（A4で約794px）になり、
 *   index.css のスマホ向け指定（html/body/#root と Amplify の枠を 100dvh・overflow:hidden に固定）が
 *   効いて本文が切れるため、#root の中では刷らない。
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent, ReactNode, Ref } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, GlobalStyles, IconButton, TextField, ThemeProvider,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import { theme } from '../../utils/colors';
import { groupContentsByTheme, listTodayContents } from '../../services/s3Service';
import type { MemoContent } from '../../services/s3Service';
import {
  buildSelectedSheet, getPaperLayout, listDayThemesByGenre, moveSelectedId, shiftDateKey, toDateKey,
} from '../../utils/meetingSheet';
import type { MeetingSheetSection, PaperLayout, PaperOrientation, PaperSize } from '../../utils/meetingSheet';
import { asErrorLike } from '../../utils/errorLike';
import { readStoredJson } from '../../utils/storage';

interface MeetingSheetPageProps {
  /** App から渡されるが本コンポーネントでは未使用 */
  user?: unknown;
  /** 同上 */
  isDesk?: boolean;
  /** 同上 */
  onSignOut?: () => void;
}

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const PAPER_STORAGE_KEY = 'meetingSheetPaper';
const selectionStorageKey = (dateKey: string) => `meetingSheetSelection_${dateKey}`;
/** 日付ごとに保存する作業状態。選んだテーマの順番と、テーマごとの掲載ページ */
interface StoredSelection {
  ids: string[];
  pages: Record<string, string>;
}
const readSelection = (dateKey: string): StoredSelection => {
  const stored = readStoredJson<unknown>(selectionStorageKey(dateKey), null);
  // 掲載ページを入れる前はIDの配列だけを保存していた
  if (Array.isArray(stored)) return { ids: stored.map(String), pages: {} };
  const obj = (stored || {}) as Partial<StoredSelection>;
  return {
    ids: Array.isArray(obj.ids) ? obj.ids.map(String) : [],
    pages: obj.pages && typeof obj.pages === 'object' ? obj.pages : {},
  };
};
const DRAG_TYPE = 'application/x-meeting-theme';
/** 用紙上の並び替え。右の一覧からの追加（DRAG_TYPE）と区別する */
const SHEET_ITEM_DRAG_TYPE = 'application/x-meeting-sheet-item';
/**
 * 用紙の余白。@page で指定し、印刷画面の余白が「デフォルト」ならこの値になる。
 * 以前は余白0で用紙いっぱいに描いていたが、印刷画面で「最小」などを選ぶと
 * 印刷できる範囲が用紙より狭くなり、本文の枠ごと2ページ目へ送られた。
 */
const PAGE_MARGIN_MM = 10;
/** 余白の設定が多少違っても1ページに収まるよう、中身の高さに持たせる余裕 */
const PAGE_SLACK_MM = 4;
/** 文字サイズの探索範囲（pt）。下限でも収まらなければ警告を出す */
const MIN_FONT_PT = 5;
const MAX_FONT_PT = 14;
const MM_TO_PX = 96 / 25.4;

const formatTitleDate = (dateKey: string): string => {
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(y!, m! - 1, d!);
  return `${y}年${m}月${d}日（${date.toLocaleDateString('ja-JP', { weekday: 'short' })}）`;
};

interface SheetPaperProps {
  layout: PaperLayout;
  title: string;
  sections: MeetingSheetSection[];
  total: number;
  fontPt: number;
  /** 文字サイズの実測に使う。画面のプレビューにだけ渡す */
  bodyRef?: Ref<HTMLDivElement>;
  /** テーマごとの掲載ページ（ユーザーが入力する） */
  pages: Record<string, string>;
  /** 渡したときだけ各テーマに外すボタンを出す（印刷用には渡さない） */
  onRemove?: (memoId: string) => void;
  /** 渡したときだけ掲載ページを入力欄にする（印刷用は文字で出す） */
  onPageChange?: (memoId: string, value: string) => void;
  /** 渡したときだけ同じ種別内での並び替え（ドラッグ・↑↓）を有効にする */
  onMove?: (memoId: string, targetId: string, after: boolean) => void;
  /** 何も選んでいないときの案内。印刷用は空のまま */
  emptyMessage?: ReactNode;
  paperProps?: Record<string, unknown>;
  paperSx?: Record<string, unknown>;
  /** 印刷用。用紙の枠と余白を描かず、中身だけを出す（余白は @page が付ける） */
  printOnly?: boolean;
}

/** 掲載ページ欄。入力欄（画面）と文字（印刷）で同じ寸法にする */
const PAGE_CELL_SX = {
  flexShrink: 0,
  width: '2.6em',
  height: '1.35em',
  boxSizing: 'border-box',
  m: 0,
  p: 0,
  border: '0.2mm solid #80868b',
  borderRadius: '0.15em',
  font: 'inherit',
  lineHeight: '1.3em',
  textAlign: 'center',
  color: '#000000',
  bgcolor: '#ffffff',
  '&::placeholder': { color: '#bdc1c6' },
  '&:focus': { outline: '0.4mm solid #1a73e8', borderColor: '#1a73e8' },
} as const;

/** 用紙1枚。画面のプレビューと印刷用の複製で同じものを使い、見た目を一致させる */
const SheetPaper = ({
  layout, title, sections, total, fontPt, bodyRef, pages, onRemove, onPageChange, onMove,
  emptyMessage, paperProps, paperSx, printOnly,
}: SheetPaperProps) => {
  // 並び替えのドラッグ中の項目と落とし先。別の種別へは落とせない
  const [dragging, setDragging] = useState<{ id: string; genre: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; after: boolean } | null>(null);
  const endDrag = () => {
    setDragging(null);
    setDropTarget(null);
  };

  return (
    <Box
      {...paperProps}
      sx={printOnly ? {} : {
        width: `${layout.widthMm}mm`,
        height: `${layout.heightMm}mm`,
        p: `${PAGE_MARGIN_MM}mm`,
        boxSizing: 'border-box',
        bgcolor: '#ffffff',
        ...paperSx,
      }}
    >
      <Box
        sx={{
          width: `${layout.widthMm - PAGE_MARGIN_MM * 2}mm`,
          height: `${layout.heightMm - PAGE_MARGIN_MM * 2 - PAGE_SLACK_MM}mm`,
          // 印刷画面で余白を「最小」「なし」にすると印刷範囲が広がるので、左に寄らないよう中央に置く
          mx: 'auto',
          overflow: 'hidden',
          bgcolor: '#ffffff',
          color: '#000000',
          textAlign: 'left',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', borderBottom: '0.6mm solid #000', pb: '1.5mm', mb: '3mm' }}>
          <Typography component="h1" sx={{ fontSize: '15pt', fontWeight: 700, lineHeight: 1.2 }}>
            {title}
          </Typography>
          {total > 0 && <Typography sx={{ fontSize: '10pt' }}>全{total}件</Typography>}
        </Box>

        {sections.length === 0 ? (
          emptyMessage && (
            <Typography sx={{ py: 8, textAlign: 'center', color: '#80868b', fontSize: '12pt' }}>
              {emptyMessage}
            </Typography>
          )
        ) : (
          <Box
            ref={bodyRef}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              fontSize: `${fontPt}pt`,
              lineHeight: 1.35,
              columnCount: layout.columns,
              columnGap: '6mm',
              columnRule: '0.2mm solid #b8c2cf',
              columnFill: 'auto',
            }}
          >
            {sections.map((section) => (
              <Box key={section.genre} component="section" sx={{ mb: '0.8em' }}>
                <Box
                  component="h2"
                  sx={{
                    m: 0,
                    mb: '0.3em',
                    fontSize: '1.1em',
                    fontWeight: 700,
                    bgcolor: '#e8eaed',
                    borderLeft: '0.5em solid #3c4043',
                    px: '0.4em',
                    py: '0.15em',
                    breakAfter: 'avoid',
                    breakInside: 'avoid',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact',
                  }}
                >
                  {section.genre}
                  <Box component="span" sx={{ fontWeight: 400, fontSize: '0.85em', ml: '0.5em' }}>
                    （{section.themes.length}件）
                  </Box>
                </Box>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {section.themes.map((t, index) => {
                    const prev = section.themes[index - 1];
                    const next = section.themes[index + 1];
                    const isDropTarget = dropTarget?.id === t.memoId;
                    return (
                      <Box
                        component="li"
                        key={t.memoId}
                        draggable={Boolean(onMove)}
                        onDragStart={onMove ? (e: DragEvent) => {
                          e.dataTransfer.setData(SHEET_ITEM_DRAG_TYPE, t.memoId);
                          e.dataTransfer.effectAllowed = 'move';
                          setDragging({ id: t.memoId, genre: section.genre });
                        } : undefined}
                        onDragOver={onMove ? (e: DragEvent) => {
                          if (!dragging || dragging.genre !== section.genre) return;
                          e.preventDefault();
                          e.stopPropagation();
                          e.dataTransfer.dropEffect = 'move';
                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          const after = e.clientY > rect.top + rect.height / 2;
                          if (dropTarget?.id !== t.memoId || dropTarget.after !== after) {
                            setDropTarget({ id: t.memoId, after });
                          }
                        } : undefined}
                        onDrop={onMove ? (e: DragEvent) => {
                          if (!dragging || dragging.genre !== section.genre) return;
                          e.preventDefault();
                          e.stopPropagation();
                          onMove(dragging.id, t.memoId, dropTarget?.after ?? false);
                          endDrag();
                        } : undefined}
                        onDragEnd={onMove ? endDrag : undefined}
                        sx={{
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.4em',
                          py: '0.15em',
                          // 外すボタンの有無で折り返し位置が変わると実測とずれるため、余白は常に取る
                          pr: '1.6em',
                          borderBottom: '0.15mm solid #c7d0db',
                          breakInside: 'avoid',
                          cursor: onMove ? 'grab' : 'default',
                          opacity: dragging?.id === t.memoId ? 0.4 : 1,
                          // 落とす位置を青い線で示す（線は影で描き、行の高さを変えない）
                          boxShadow: isDropTarget
                            ? `inset 0 ${dropTarget!.after ? '-0.5mm' : '0.5mm'} 0 #1a73e8`
                            : 'none',
                          '&:hover .meeting-sheet-controls': { opacity: 1 },
                          '&:hover .meeting-sheet-move': { display: 'inline-flex' },
                        }}
                      >
                        {/* 掲載ページ。入力欄と印刷で幅を揃え、折り返し位置が変わらないようにする */}
                        {onPageChange ? (
                          <Box
                            component="input"
                            value={pages[t.memoId] || ''}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => onPageChange(t.memoId, e.target.value)}
                            aria-label={`${t.themeName} の掲載ページ`}
                            placeholder="面"
                            maxLength={4}
                            sx={PAGE_CELL_SX}
                          />
                        ) : (
                          <Box component="span" sx={PAGE_CELL_SX}>{pages[t.memoId] || ''}</Box>
                        )}
                        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>{t.themeName}</Box>
                        {onRemove && (
                          // 操作ボタンはテーマ名の上に重ねて出す。行の幅を取ると印刷と折り返しがずれるため
                          <Box
                            className="meeting-sheet-controls"
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              display: 'flex',
                              alignItems: 'center',
                              bgcolor: '#ffffff',
                              opacity: 0.35,
                            }}
                          >
                            {onMove && (
                              <>
                                <IconButton
                                  className="meeting-sheet-move"
                                  aria-label="上へ"
                                  disabled={!prev}
                                  onClick={() => prev && onMove(t.memoId, prev.memoId, false)}
                                  sx={{ display: 'none', p: '0.1em', fontSize: '1em', color: '#1a73e8' }}
                                >
                                  <ArrowUpwardIcon sx={{ fontSize: '1.2em' }} />
                                </IconButton>
                                <IconButton
                                  className="meeting-sheet-move"
                                  aria-label="下へ"
                                  disabled={!next}
                                  onClick={() => next && onMove(t.memoId, next.memoId, true)}
                                  sx={{ display: 'none', p: '0.1em', fontSize: '1em', color: '#1a73e8' }}
                                >
                                  <ArrowDownwardIcon sx={{ fontSize: '1.2em' }} />
                                </IconButton>
                              </>
                            )}
                            <IconButton
                              aria-label="用紙から外す"
                              onClick={() => onRemove(t.memoId)}
                              sx={{ p: '0.1em', fontSize: '1em', color: '#c5221f' }}
                            >
                              <CloseIcon sx={{ fontSize: '1.2em' }} />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const MeetingSheetPage = (_props: MeetingSheetPageProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramDate = searchParams.get('date') || '';
  const dateKey = DATE_KEY_PATTERN.test(paramDate) ? paramDate : toDateKey(new Date());

  const [contents, setContents] = useState<MemoContent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paper, setPaper] = useState<{ size: PaperSize; orientation: PaperOrientation }>(
    () => {
      const stored = readStoredJson<{ size?: unknown; orientation?: unknown }>(PAPER_STORAGE_KEY, {});
      return {
        size: stored.size === 'A3' ? 'A3' : 'A4',
        orientation: stored.orientation === 'landscape' ? 'landscape' : 'portrait',
      };
    }
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pages, setPages] = useState<Record<string, string>>({});
  const [dragOver, setDragOver] = useState(false);
  const [fontPt, setFontPt] = useState(MAX_FONT_PT);
  const [overflowing, setOverflowing] = useState(false);
  const [previewScale, setPreviewScale] = useState(1);
  const bodyRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const layout = getPaperLayout(paper.size, paper.orientation);

  const fetchContents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setContents(await listTodayContents());
    } catch (err) {
      setError(asErrorLike(err).message || 'メモの取得に失敗しました');
      console.error('会議資料のメモ取得エラー:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchContents();
  }, [fetchContents]);

  // 日付を変えたら、その日に選んでいた内容を読み直す
  useEffect(() => {
    const stored = readSelection(dateKey);
    setSelectedIds(stored.ids);
    setPages(stored.pages);
  }, [dateKey]);

  const saveSelection = (next: StoredSelection) => {
    try {
      localStorage.setItem(selectionStorageKey(dateKey), JSON.stringify(next));
    } catch {
      // 保存できなくても印刷はできる
    }
  };
  const updateSelection = (next: string[]) => {
    setSelectedIds(next);
    saveSelection({ ids: next, pages });
  };
  const changePage = (id: string, value: string) => {
    const next = { ...pages, [id]: value };
    if (!value) delete next[id];
    setPages(next);
    saveSelection({ ids: selectedIds, pages: next });
  };
  const clearAll = () => {
    setSelectedIds([]);
    setPages({});
    saveSelection({ ids: [], pages: {} });
  };
  const addThemes = (ids: string[]) => {
    const toAdd = ids.filter((id) => !selectedIds.includes(id));
    if (toAdd.length) updateSelection([...selectedIds, ...toAdd]);
  };
  const removeTheme = (id: string) => updateSelection(selectedIds.filter((x) => x !== id));
  const toggleTheme = (id: string) => (selectedIds.includes(id) ? removeTheme(id) : addThemes([id]));

  // メモ画面のカレンダーと同じく、絞り込みをかける前の全テーマから組み立てる
  const daySections = useMemo(
    () => listDayThemesByGenre(groupContentsByTheme(contents), dateKey),
    [contents, dateKey]
  );
  const sections = useMemo(() => buildSelectedSheet(daySections, selectedIds), [daySections, selectedIds]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const total = sections.reduce((sum, s) => sum + s.themes.length, 0);
  const dayTotal = daySections.reduce((sum, s) => sum + s.themes.length, 0);

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const id = e.dataTransfer.getData(DRAG_TYPE);
    if (id) addThemes([id]);
  };

  // 1枚に収まる最大の文字サイズを二分探索で決める。
  // 段組みは column-fill:auto なので、溢れると右に段が増えて scrollWidth が広がる
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const fits = (pt: number) => {
      el.style.fontSize = `${pt}pt`;
      return el.scrollWidth <= el.clientWidth + 1 && el.scrollHeight <= el.clientHeight + 1;
    };
    let lo = MIN_FONT_PT;
    let hi = MAX_FONT_PT;
    if (fits(hi)) {
      lo = hi;
    } else {
      for (let i = 0; i < 12; i += 1) {
        const mid = (lo + hi) / 2;
        if (fits(mid)) lo = mid; else hi = mid;
      }
    }
    const best = Math.floor(lo * 10) / 10;
    setOverflowing(!fits(best));
    el.style.fontSize = '';
    setFontPt(best);
  }, [sections, paper]);

  // 画面の幅に収まるようにプレビューだけ縮小する（印刷には影響しない）
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const update = () => {
      const available = el.clientWidth - 32;
      setPreviewScale(Math.min(1, available / (layout.widthMm * MM_TO_PX)));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [layout.widthMm]);

  const changeDate = (next: string) => {
    if (DATE_KEY_PATTERN.test(next)) setSearchParams({ date: next });
  };

  const changePaper = (next: Partial<typeof paper>) => {
    const merged = { ...paper, ...next };
    setPaper(merged);
    try {
      localStorage.setItem(PAPER_STORAGE_KEY, JSON.stringify(merged));
    } catch {
      // 保存できなくても印刷はできる
    }
  };

  useEffect(() => {
    document.title = `会議資料 ${dateKey}`;
  }, [dateKey]);

  const paperWidthPx = layout.widthMm * MM_TO_PX;
  const paperHeightPx = layout.heightMm * MM_TO_PX;

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles
        styles={{
          // 用紙そのものを1ページとして刷るため、ブラウザ側の余白は0にする
          '@page': { size: `${paper.size} ${paper.orientation}`, margin: `${PAGE_MARGIN_MM}mm` },
          '.meeting-sheet-print-root': { display: 'none' },
          '@media print': {
            // index.css の 100dvh 固定より強くするため詳細度を上げる
            'html:root, html:root body': {
              height: 'auto !important',
              minHeight: '0 !important',
              maxHeight: 'none !important',
              overflow: 'visible !important',
            },
            'html:root body > #root': { display: 'none !important' },
            '.meeting-sheet-print-root': { display: 'block !important' },
          },
        }}
      />
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
        <Box
         
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            px: 2,
            py: 1.25,
            bgcolor: '#ffffff',
            borderBottom: '1px solid #d6dce5',
          }}
        >
          <Typography sx={{ fontWeight: 700, mr: 1 }}>会議資料</Typography>
          <Tooltip title="前日">
            <IconButton size="small" onClick={() => changeDate(shiftDateKey(dateKey, -1))}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <TextField
            type="date"
            size="small"
            value={dateKey}
            onChange={(e) => changeDate(e.target.value)}
            sx={{ width: 170 }}
          />
          <Tooltip title="翌日">
            <IconButton size="small" onClick={() => changeDate(shiftDateKey(dateKey, 1))}>
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
          <Button size="small" variant="outlined" onClick={() => changeDate(toDateKey(new Date()))}>
            今日
          </Button>
          <Tooltip title="再読み込み">
            <span>
              <IconButton size="small" onClick={() => void fetchContents()} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Box sx={{ flex: 1 }} />

          <ToggleButtonGroup
            size="small"
            exclusive
            value={paper.size}
            onChange={(_e, v: PaperSize | null) => v && changePaper({ size: v })}
          >
            <ToggleButton value="A4">A4</ToggleButton>
            <ToggleButton value="A3">A3</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={paper.orientation}
            onChange={(_e, v: PaperOrientation | null) => v && changePaper({ orientation: v })}
          >
            <ToggleButton value="portrait">縦</ToggleButton>
            <ToggleButton value="landscape">横</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body2" sx={{ color: '#5f6368', minWidth: 72 }}>
            文字 {fontPt}pt
          </Typography>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()} disabled={loading}>
            印刷
          </Button>
        </Box>

        {(error || overflowing) && (
          <Box sx={{ px: 2, pt: 1.5 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {overflowing && !loading && (
              <Alert severity="warning">
                最小の文字サイズ（{MIN_FONT_PT}pt）でも1枚に収まりません。用紙をA3にするか、横向きにしてください。
              </Alert>
            )}
          </Box>
        )}

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          <Box
            ref={scrollRef}
            sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto', bgcolor: '#eef1f5', p: 2 }}
          >
            {/* 縮小したプレビューの実寸に合わせた枠。transform は占有サイズを変えないため */}
            <Box
              sx={{ width: paperWidthPx * previewScale, height: paperHeightPx * previewScale, mx: 'auto' }}
            >
              <SheetPaper
                layout={layout}
                title={`${formatTitleDate(dateKey)}　会議メモ`}
                sections={sections}
                total={total}
                fontPt={fontPt}
                bodyRef={bodyRef}
                pages={pages}
                onRemove={removeTheme}
                onPageChange={changePage}
                onMove={(id, targetId, after) => updateSelection(moveSelectedId(selectedIds, id, targetId, after))}
                emptyMessage="右の一覧からテーマをクリックするか、ここへドラッグして入れてください"
                paperProps={{
                  onDragOver: (e: DragEvent) => {
                    if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'copy';
                    setDragOver(true);
                  },
                  onDragLeave: () => setDragOver(false),
                  onDrop: handleDrop,
                }}
                paperSx={{
                  border: '1px solid #c7d0db',
                  outline: dragOver ? '3px dashed #1a73e8' : 'none',
                  outlineOffset: '-3px',
                  boxShadow: '0 2px 8px rgba(60,64,67,0.15)',
                  transform: `scale(${previewScale})`,
                  transformOrigin: 'top left',
                }}
              />
            </Box>
          </Box>

          {/* テーマの選択一覧（印刷しない） */}
          <Box
           
            sx={{
              width: { xs: '100%', md: 380 },
              flexShrink: 0,
              maxHeight: { xs: '45%', md: 'none' },
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#ffffff',
              borderLeft: { md: '1px solid #c7d0db' },
              borderTop: { xs: '1px solid #c7d0db', md: 'none' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: '1px solid #d6dce5' }}>
              <Typography sx={{ fontWeight: 700, flex: 1 }}>
                この日のテーマ
                <Box component="span" sx={{ fontWeight: 400, fontSize: '0.85rem', color: '#5f6368', ml: 1 }}>
                  {selectedIds.length > 0 ? `${total} / ${dayTotal}件を選択` : `${dayTotal}件`}
                </Box>
              </Typography>
              <Button size="small" color="error" onClick={clearAll} disabled={selectedIds.length === 0}>
                クリア
              </Button>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : daySections.length === 0 ? (
                !error && <Typography sx={{ py: 6, textAlign: 'center', color: '#5f6368' }}>この日のテーマはありません</Typography>
              ) : (
                daySections.map((section) => {
                  const unselected = section.themes.filter((t) => !selectedSet.has(t.memoId));
                  return (
                    <Box key={section.genre}>
                      <Box
                        sx={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          px: 2,
                          py: 0.75,
                          bgcolor: '#e8eaed',
                          borderBottom: '1px solid #c7d0db',
                        }}
                      >
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', flex: 1 }}>
                          {section.genre}
                          <Box component="span" sx={{ fontWeight: 400, color: '#5f6368', ml: 0.75 }}>
                            {section.themes.length - unselected.length}/{section.themes.length}
                          </Box>
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => addThemes(unselected.map((t) => t.memoId))}
                          disabled={unselected.length === 0}
                          sx={{ py: 0, minWidth: 0 }}
                        >
                          全部入れる
                        </Button>
                      </Box>
                      {section.themes.map((t) => {
                        const selected = selectedSet.has(t.memoId);
                        return (
                          <Box
                            key={t.memoId}
                            role="button"
                            tabIndex={0}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData(DRAG_TYPE, t.memoId);
                              e.dataTransfer.effectAllowed = 'copy';
                            }}
                            onClick={() => toggleTheme(t.memoId)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleTheme(t.memoId);
                              }
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              px: 2,
                              py: 0.9,
                              cursor: 'pointer',
                              borderBottom: '1px solid #e3e6ea',
                              bgcolor: selected ? '#e8f0fe' : '#ffffff',
                              color: selected ? '#174ea6' : '#202124',
                              fontSize: '0.9rem',
                              lineHeight: 1.5,
                              userSelect: 'none',
                              '&:hover': { bgcolor: selected ? '#d2e3fc' : '#f1f3f4' },
                              '&:focus-visible': { outline: '2px solid #1a73e8', outlineOffset: '-2px' },
                            }}
                          >
                            {selected
                              ? <CheckBoxIcon sx={{ fontSize: 20, color: '#1a73e8', mt: '1px' }} />
                              : <CheckBoxOutlineBlankIcon sx={{ fontSize: 20, color: '#80868b', mt: '1px' }} />}
                            <Box component="span" sx={{ flex: 1, minWidth: 0, wordBreak: 'break-all' }}>{t.themeName}</Box>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {createPortal(
        <Box className="meeting-sheet-print-root">
          <SheetPaper
            layout={layout}
            title={`${formatTitleDate(dateKey)}　会議メモ`}
            sections={sections}
            total={total}
            fontPt={fontPt}
            pages={pages}
            printOnly
          />
        </Box>,
        document.body
      )}
    </ThemeProvider>
  );
};

export default MeetingSheetPage;
